import {
  Children,
  Fragment,
  isValidElement,
  type JSXElementConstructor,
  type ReactElement,
  type ReactNode,
} from 'react';

import { z } from 'zod';

const isFragment = (child: ReactNode): child is ReactElement =>
  isValidElement(child) && child.type === Fragment;

/**
 * Returns whether or not the provided node, {@link ReactNode}, is an empty React fragment (i.e.
 * <></>).
 */
export const isEmptyFragment = (children: ReactNode) =>
  Children.toArray(children).some(child => {
    if (isFragment(child)) {
      const childProps = child.props as { children?: ReactNode };
      return (
        childProps.children === undefined ||
        (Array.isArray(childProps.children) && childProps.children.length === 0)
      );
    }
    return false;
  });

export type ReactChildArray = ReturnType<typeof Children.toArray>;

const isRenderableIfPrimitive = (node: ReactNode) =>
  node !== null &&
  node !== undefined &&
  typeof node !== 'boolean' &&
  (typeof node !== 'string' || node.trim() !== '');

/**
 * Recursively fragments that may exist inside of a JSX block such that the resulting structure
 * does not contain any fragments.
 *
 * @param {ReactNode} children
 *   The {@link ReactNode} structure that should be flattened.
 *
 * @example
 * // Returns ['Foo', <div>Bar</div>]
 * flattenChildren(<>Foo<><div>Bar</div></></>)
 */
export const flattenChildren = (children: ReactNode): ReactChildArray => {
  if (typeof children === 'string') {
    return children.trim() === '' ? [] : [children];
  }
  const childrenArray = Children.toArray(children);

  return childrenArray.reduce((flatChildren: ReactChildArray, child) => {
    if (isFragment(child)) {
      const childProps = child.props as { children?: ReactNode };
      if (isRenderableIfPrimitive(childProps.children)) {
        return [...flatChildren, ...flattenChildren(childProps.children)];
      }
      return flatChildren;
    } else if (isRenderableIfPrimitive(child)) {
      return [...flatChildren, child];
    }
    return flatChildren;
  }, [] as ReactChildArray);
};

export const nodeHasRenderableContent = (node: ReactNode) =>
  flattenChildren(node).length !== 0 && isRenderableIfPrimitive(node);

export const isReactNode = (value: unknown): value is ReactNode =>
  typeof value === 'string' ||
  typeof value === 'number' ||
  isValidElement(value) ||
  (Array.isArray(value) && value.every(isReactNode)) ||
  value === null ||
  value === undefined ||
  typeof value === 'boolean';

export const ReactNodeSchema = z.custom<ReactNode>(isReactNode);

/**
 * Safely determines whether or not the provided {@link ReactNode} is a valid React element and has
 * a display name or name property.  If the provided {@link ReactNode} is in fact a valid React
 * element, and has an obtainable display name/name property, the value of that property will be
 * returned.
 *
 * @param {ReactNode} child The child element for which the display name should be parsed.
 *
 * @returns {string | null}
 *   The name or display name of the child element, {@link ReactNode}, if it exists.
 */
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export const parseElementType = (child: ReactNode): JSXElementConstructor<any> | null | string => {
  if (isValidElement(child)) {
    return child.type;
  }
  return null;
};

type ElementsToFind<V = unknown> = Record<string, V>;

type InferredKey<F extends ElementsToFind> =
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  F extends Record<infer K extends string, any> ? K : never;

type FoundElements<F extends ElementsToFind> = Partial<Record<InferredKey<F>, ReactNode>>;

export const parseElementsByType = <F extends ElementsToFind<V>, V>(
  content: ReactNode | ReactNode[],
  lookup: F,
): [FoundElements<F>, ReactNode[]] => {
  const children = Array.isArray(content) ? content : [content];
  let found: FoundElements<F> = {};

  let rest: ReactNode[] = children;

  for (const key in lookup) {
    const component = lookup[key];
    const ch = children.filter(child => isValidElement(child) && child.type === component);
    if (ch.length >= 1) {
      found = { ...found, [key]: ch.length === 1 ? ch[0] : ch };
      rest = rest.filter(child => !isValidElement(child) || child.type !== component);
    }
  }
  return [found, rest];
};
